import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Package, ChevronRight, Clock, CheckCircle, XCircle } from "lucide-react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { formatPrice, displayColor } from "../utils/formatters";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrdersPage() {
  const { isAuthenticated, token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchOrders();
    }
  }, [isAuthenticated, token]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(response.data);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-32 pb-24 px-6 md:px-12 flex items-center justify-center" data-testid="orders-login-prompt">
        <div className="text-center">
          <Package className="w-16 h-16 mx-auto text-[#A3A3A3] mb-6" />
          <h2 className="font-heading text-3xl mb-4">LOGIN TO VIEW ORDERS</h2>
          <p className="text-[#A3A3A3] mb-8">Please login to access your order history</p>
          <Link to="/auth" className="btn-primary inline-block" data-testid="login-btn">
            Login
          </Link>
        </div>
      </div>
    );
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case "confirmed":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "pending":
        return <Clock className="w-5 h-5 text-[#FFD700]" />;
      case "cancelled":
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Package className="w-5 h-5 text-[#A3A3A3]" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "text-green-500";
      case "pending":
        return "text-[#FFD700]";
      case "cancelled":
        return "text-red-500";
      default:
        return "text-[#A3A3A3]";
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-24 px-6 md:px-12" data-testid="orders-page">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <p className="font-mono text-[#FFD700] text-sm tracking-[0.3em] mb-2">
            YOUR PURCHASES
          </p>
          <h1 className="font-heading text-5xl md:text-7xl tracking-tight uppercase">
            ORDERS
          </h1>
        </motion.div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="loader" />
          </div>
        ) : orders.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <Package className="w-16 h-16 mx-auto text-[#A3A3A3] mb-6" />
            <h2 className="font-heading text-2xl mb-4">NO ORDERS YET</h2>
            <p className="text-[#A3A3A3] mb-8">Start shopping to see your orders here</p>
            <Link to="/shop" className="btn-primary inline-flex items-center gap-3" data-testid="start-shopping-btn">
              Start Shopping
              <ChevronRight className="w-5 h-5" />
            </Link>
          </motion.div>
        ) : (
          <div className="space-y-6">
            {orders.map((order, index) => (
              <motion.div
                key={order.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="border border-white/10 p-6 md:p-8 hover:border-[#FFD700]/30 transition-colors"
                data-testid={`order-${order.id}`}
              >
                {/* Order Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pb-6 border-b border-white/10">
                  <div>
                    <p className="font-mono text-[#A3A3A3] text-xs tracking-wider">
                      ORDER ID
                    </p>
                    <p className="font-mono text-sm mt-1">{order.id.slice(0, 8).toUpperCase()}</p>
                  </div>
                  <div>
                    <p className="font-mono text-[#A3A3A3] text-xs tracking-wider">
                      DATE
                    </p>
                    <p className="font-mono text-sm mt-1">
                      {new Date(order.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="font-mono text-[#A3A3A3] text-xs tracking-wider">
                      STATUS
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      {getStatusIcon(order.status)}
                      <span className={`font-mono text-sm uppercase ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="font-mono text-[#A3A3A3] text-xs tracking-wider">
                      TOTAL
                    </p>
                    <p className="font-mono text-[#FFD700] text-lg mt-1">
                      {formatPrice(order.total)}
                    </p>
                  </div>
                </div>

                {/* Order Items */}
                <div className="space-y-4">
                  {order.items.map((item, itemIndex) => (
                    <div
                      key={itemIndex}
                      className="flex items-center gap-4"
                    >
                      <div className="w-16 h-20 bg-[#1A1A1A] flex items-center justify-center">
                        <Package className="w-6 h-6 text-[#A3A3A3]" />
                      </div>
                      <div className="flex-1">
                        <p className="font-heading text-lg">{item.product_name}</p>
                        <p className="text-[#A3A3A3] text-sm">
                          Size: {item.size} / Color: {displayColor(item.color)} / Qty: {item.quantity}
                        </p>
                      </div>
                      <p className="font-mono text-[#FFD700]">
                        {formatPrice(item.price * item.quantity)}
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
